import type {
  AxfoodBasicProductViewModel,
  AxfoodBasicWishListViewModel,
  AxfoodProductDetailsViewModel,
  WishlistEntryForm,
} from '@occ/api-client';
import paths, { LoginQueryParams } from '@constants/paths';
import { useEffect, useState } from 'react';
import Button from '@atoms/Button/Button';
import Heading from '@atoms/Heading/Heading';
import useWishlists from '@hooks/useWishlists';
import {
  StyledButton,
  StyledButtonWrapper,
  StyledIcon,
  StyledModalContentWrapper,
  StyledProductListItem,
  StyledText,
  StyledWishlistRow,
  StyledWishlistRowText,
  StyledWishlistWrapper,
  StyledWishlistWrapperTopSection,
  StyledAddToListComponentModal,
} from './AddToListComponent.styles';
import Text from '@atoms/Text/Text';
import { addToUserWishlist, createNewWishList } from '@api/interfaces/wishlistApi';
import Spinner from '@molecules/Spinner/Spinner';
import { FormMode } from '@organisms/AddToListDropdown/types';
import { useTranslations } from 'next-intl';
import useSnackBar from '@hooks/useSnackbar';
import CreateNewListForm from '@organisms/AddToListDropdown/CreateNewListForm';
import {
  trackRedirectToLoginFromAddToList,
  trackShowSaveToWishListModal,
} from '@helpers/analyticsHelpers/trackWishList';
import useCustomer from '@hooks/useCustomer';
import Validation from '@icons/validation.svg';
import List from '@icons/list.svg';
import useCart from '@hooks/useCart';
import { useRouter, useSearchParams } from 'next/navigation';

interface AddToListCallback {
  option: AxfoodBasicWishListViewModel;
  productEntries: Array<WishlistEntryForm>;
  tracking?: {
    parent: string;
  };
}

interface CreateNewListCallback {
  inputValue: string;
}

interface Props {
  product?: AxfoodProductDetailsViewModel | AxfoodBasicProductViewModel;
  productEntries: Array<WishlistEntryForm>;
  addToListCallback?: ({ option, productEntries, tracking }: AddToListCallback) => void;
  createNewListCallback?: ({ inputValue }: CreateNewListCallback) => void;
  onClose: () => void;
  isOpen: boolean;
  queryParamsIfNotLoggedIn?: {
    from: LoginQueryParams;
  };
  tracking?: {
    parent: string;
  };
}

const AddToListComponent = ({
  product,
  queryParamsIfNotLoggedIn = { from: LoginQueryParams.ADD_TO_LIST },
  productEntries,
  tracking,
  addToListCallback,
  createNewListCallback,
  onClose,
  isOpen,
}: Props) => {
  const t = useTranslations('addToList');
  const { setSnack } = useSnackBar();
  const { customer } = useCustomer();
  const { wishlists, error: wishlistError, isLoading, refreshWishlists } = useWishlists(customer?.uid);
  const { cart } = useCart();
  const [formMode, setFormMode] = useState<FormMode>('ADD');
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentQuery = searchParams ? Object.fromEntries(searchParams.entries()) : {};

  const [selectedWishListRow, setSelectedWishListRow] = useState<AxfoodBasicWishListViewModel>(
    {} as AxfoodBasicWishListViewModel
  );
  const [quantity, setQuantity] = useState(productEntries[0]?.quantity ?? 0);
  const [isFetching, setIsFetching] = useState(false);
  const [isCreatingNewList, setIsCreatingNewList] = useState(false);

  const saveToList = async () => {
    // Compute entries at save time to ensure we have the latest data
    // For single product (with quantity UI), apply the adjusted quantity to the first entry
    // For cart save (no product prop), use productEntries directly
    const entriesToSave = product
      ? productEntries.map((entry, i) => (i === 0 ? { ...entry, quantity } : entry))
      : productEntries;

    try {
      setIsFetching(true);
      await addToUserWishlist(selectedWishListRow.id, entriesToSave);

      if (addToListCallback) {
        addToListCallback({ option: selectedWishListRow, productEntries: entriesToSave });
      }

      setIsFetching(false);
      setFormMode('COMPLETE');
      onClose();
      setSnack({
        text: productEntries.length === 1 ? t('snackBar.text') : t('snackBar.textPlural'),
        icon: 'valid',
        link: `${paths.MY_LISTS}/${selectedWishListRow.id}`,
        linkText: t('snackBar.linkText'),
      });
    } catch (_err) {
      setSnack({
        text: t('error.onSave'),
        icon: null,
      });
    } finally {
      setIsFetching(false);
    }
  };

  const handleNotLoggedIn = () => {
    const newQuery = {
      ...currentQuery,
      ...queryParamsIfNotLoggedIn,
    };
    const queryString = new URLSearchParams(newQuery as any).toString();
    const newPath = queryString ? `${paths.USER_LOGIN}?${queryString}` : paths.USER_LOGIN;

    router.push(newPath);

    if (tracking?.parent) {
      trackRedirectToLoginFromAddToList(tracking.parent);
    }

    onClose();
  };

  const onSaveToListModalOpen = async () => {
    await refreshWishlists();
    if (customer?.isAnonymous) {
      handleNotLoggedIn();
    }

    if (tracking?.parent) {
      trackShowSaveToWishListModal(tracking.parent);
    }
  };

  const onCreateNewListHandler = async (inputValue: string) => {
    setIsCreatingNewList(true);
    try {
      await createNewWishList({
        name: inputValue,
        entries: [],
      });

      await refreshWishlists();
      if (createNewListCallback) {
        createNewListCallback({ inputValue });
      }

      setFormMode('ADD');
    } catch (_err) {
      setSnack({
        text: t('error.onCreate'),
        icon: null,
      });
    } finally {
      setIsCreatingNewList(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSelectedWishListRow({} as AxfoodBasicWishListViewModel);
      setFormMode('ADD');
    }
    if (isOpen) {
      onSaveToListModalOpen();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, cart?.products]);

  useEffect(() => {
    if (wishlists && wishlists.length > 0) setSelectedWishListRow(wishlists[0]);
  }, [wishlists]);

  return (
    <>
      {isOpen && !wishlistError && (
        <StyledAddToListComponentModal
          noPadding
          size="medium"
          hasCloseIcon
          closeOnEscape
          onClose={onClose}
          confirmDisabled={!selectedWishListRow.id}
          isSubmitting={isLoading}
        >
          {isLoading && <Spinner />}
          {!isLoading && (
            <>
              <StyledModalContentWrapper>
                <Heading variant="h2" size="small">
                  {t('heading.unsaved')}
                </Heading>
                {product && (
                  <StyledProductListItem
                    clickable={false}
                    // Force product to be in stock, should always be able to change quantity
                    product={{ ...product, outOfStock: false }}
                    quantity={quantity}
                    quantityCallback={(quantity) => {
                      setQuantity(quantity);
                    }}
                    variant="addToList"
                  />
                )}

                <StyledWishlistWrapper>
                  <StyledWishlistWrapperTopSection>
                    <StyledText type={'subtitle'} size="small">
                      {formMode === 'NEW' ? t('heading.new') : t('heading.choose')}
                    </StyledText>
                    {formMode !== 'NEW' && (
                      <Button variant="link" data-testid="create-new-list" onClick={() => setFormMode('NEW')}>
                        {t('heading.new')}
                      </Button>
                    )}
                  </StyledWishlistWrapperTopSection>
                  {formMode === 'NEW' && (
                    <CreateNewListForm onCreateNewList={onCreateNewListHandler} isSaving={isCreatingNewList} />
                  )}

                  {wishlists &&
                    wishlists.length > 0 &&
                    wishlists?.map((wishlist) => {
                      const selected = selectedWishListRow.id === wishlist.id;
                      return (
                        <StyledWishlistRow
                          data-testid="wishlist-option"
                          variant="transparent"
                          onClick={() => {
                            setSelectedWishListRow(wishlist);
                          }}
                          isSelected={selected}
                          key={wishlist.id}
                        >
                          <StyledIcon
                            color={selected ? 'highlight' : 'black'}
                            svg={selected ? Validation : List}
                            aria-label={selected ? t('selectedLabel') : t('notSelectedLabel')}
                            size={24}
                          />
                          <StyledWishlistRowText type="subtitle">
                            {wishlist.name} ({wishlist.numberOfProducts})
                          </StyledWishlistRowText>
                        </StyledWishlistRow>
                      );
                    })}
                  {wishlists && wishlists.length <= 0 && (
                    <Text type="body" size="medium">
                      {t('empty')}
                    </Text>
                  )}
                </StyledWishlistWrapper>
              </StyledModalContentWrapper>
              <StyledButtonWrapper>
                <StyledButton variant="secondary" onClick={onClose}>
                  {t('cancel')}
                </StyledButton>
                <StyledButton
                  variant="primary"
                  onClick={saveToList}
                  disabled={!selectedWishListRow.id || (product && !quantity)}
                  isSubmitting={isFetching}
                >
                  {t('saveToList')}
                </StyledButton>
              </StyledButtonWrapper>
            </>
          )}
        </StyledAddToListComponentModal>
      )}
    </>
  );
};

export default AddToListComponent;
